// Billing DTOs — 02b (FOL/CHG/PAY/DEP/RFD/ADJ/IVC/CRN/CSH/NAU), PRD §3.8 CHKO.
import { z } from 'zod';
import { dateSchema, moneySchema, uuidSchema } from './common.js';
const amount4 = moneySchema.shape.amount;
const positive4 = amount4.refine((a) => !a.startsWith('-') && a !== '0.0000', 'must be > 0');
const iso4217 = z.string().regex(/^[A-Z]{3}$/);
const reasonCode = z.string().trim().min(2).max(40);
const reasonText = z.string().trim().max(500).optional();
export const paymentMethodSchema = z.enum(['cash', 'card', 'bank_transfer', 'other']);
export const openWindowSchema = z.object({
    ownerType: z.enum(['guest', 'company', 'house']).default('guest'),
    ownerGuestId: uuidSchema.optional(),
    ownerLabel: z.string().trim().max(80).optional(),
    currency: iso4217.optional(),
});
export const postChargeSchema = z.object({
    chargeCodeId: uuidSchema,
    quantity: z
        .string()
        .regex(/^\d+(\.\d{1,4})?$/)
        .default('1'),
    /** unit price in `currency`; omitted → charge code default price */
    unitPrice: amount4.optional(),
    currency: iso4217.optional(),
    descriptionAr: z.string().trim().max(200).optional(),
    descriptionEn: z.string().trim().max(200).optional(),
    reference: z.string().trim().max(80).optional(),
    reasonText: reasonText,
});
export const reverseLineSchema = z.object({ reasonCode, reasonText });
export const transferLinesSchema = z.object({
    lineIds: z.array(uuidSchema).min(1).max(200),
    toFolioId: uuidSchema,
});
export const postPaymentSchema = z.object({
    kind: z.enum(['payment', 'deposit']).default('payment'),
    method: paymentMethodSchema,
    tenderCurrency: iso4217,
    tenderAmount: positive4,
    /** cash only: currency the change is handed back in (default = tender currency) */
    changeCurrency: iso4217.optional(),
    methodDetail: z.string().trim().max(80).optional(),
    receivedFromText: z.string().trim().max(120).optional(),
    reference: z.string().trim().max(80).optional(),
});
export const refundRequestSchema = z.object({
    /** in folio currency */
    amount: positive4,
    method: paymentMethodSchema,
    tenderCurrency: iso4217.optional(),
    methodDetail: z.string().trim().max(80).optional(),
    reasonCode,
    reasonText,
});
export const discountSchema = z
    .object({
    lineId: uuidSchema,
    percent: z
        .string()
        .regex(/^\d{1,3}(\.\d{1,4})?$/)
        .optional(),
    amount: positive4.optional(),
    reasonCode,
    reasonText,
})
    .refine((v) => (v.percent ? !v.amount : !!v.amount), 'percent or amount');
export const approvalDecisionSchema = z.object({
    decision: z.enum(['approve', 'reject']),
    note: z.string().trim().max(300).optional(),
});
export const shiftOpenSchema = z.object({
    floats: z
        .array(z.object({ currency: iso4217, amount: amount4 }))
        .max(10)
        .default([]),
    notes: z.string().trim().max(300).optional(),
});
export const shiftCloseSchema = z.object({
    counted: z
        .array(z.object({ currency: iso4217, amount: amount4 }))
        .min(1)
        .max(10),
    varianceReason: z.string().trim().max(300).optional(),
    /** supervisor closing another user's shift (BR-CSH workflow 6) */
    shiftId: uuidSchema.optional(),
});
export const shiftReopenSchema = z.object({ reason: z.string().trim().min(3).max(300) });
export const invoiceSeriesInputSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z0-9_-]{1,16}$/),
    kind: z.enum(['final', 'proforma', 'credit_note']),
    prefix: z.string().trim().max(20).default(''),
});
export const billToSchema = z.object({
    nameAr: z.string().trim().min(1).max(160),
    nameEn: z.string().trim().max(160).optional(),
    address: z.string().trim().max(300).optional(),
    taxId: z.string().trim().max(40).optional(),
});
export const issueInvoiceSchema = z.object({
    kind: z.enum(['proforma', 'final']),
    seriesId: uuidSchema.optional(),
    billToType: z.enum(['guest', 'company']).default('guest'),
    billTo: billToSchema.optional(),
    /** v1.1 AR: the company the invoice is charged to (set by the AR transfer) */
    billToCompanyAccountId: uuidSchema.optional(),
    /** default: every uninvoiced line on the folio */
    lineIds: z.array(uuidSchema).max(500).optional(),
    notesAr: z.string().trim().max(500).optional(),
    notesEn: z.string().trim().max(500).optional(),
});
export const creditNoteSchema = z
    .object({
    kind: z.enum(['full', 'partial']),
    reasonCode,
    reasonText,
    /** partial: amounts per invoice line (folio currency) */
    lines: z
        .array(z.object({ invoiceLineId: uuidSchema, amount: positive4 }))
        .max(500)
        .optional(),
})
    .refine((v) => v.kind === 'full' || (v.lines && v.lines.length > 0), 'partial needs lines');
export const nightAuditOverrideSchema = z.object({
    check: z.enum(['PC-01', 'PC-02', 'PC-03', 'PC-04']),
    reason: z.string().trim().min(3).max(300),
});
/** the client names the date it is closing (REQ-NAU-04: a closed date is refused, a stale wizard cannot roll twice) */
export const nightAuditStartSchema = z.object({ businessDate: dateSchema.optional() });
export const adminRolloverSchema = z.object({ reason: z.string().trim().min(3).max(300) });
export const checkOutSchema = z.object({
    /** stay departs before its expected date: shorten first (BR-CHKO-03) */
    earlyDeparture: z.boolean().default(false),
});
export const reportDateSchema = z.object({ businessDate: dateSchema.optional() });
export const reportRangeSchema = z.object({ from: dateSchema, to: dateSchema });
//# sourceMappingURL=billing.js.map